export const identifyingVariables = {
  id: "si-2",
  title: "Identifying Variables",
  description: "Distinguish between independent, dependent, and controlled variables in experiments",
  passages: [
    {
      id: "si-2-1",
      title: "Fertilizer and Crop Yield",
      context: "A farmer tests whether nitrogen fertilizer increases corn yield. She divides a field into 20 plots of equal size. Ten plots receive nitrogen fertilizer at a rate of 150 kg/hectare, while ten plots receive no fertilizer. All plots are planted with the same corn variety, receive equal irrigation, and are harvested at the same time. Yield (kg of corn per plot) is measured and compared between fertilized and unfertilized plots.",
      questions: [
        {
          question: "What is the independent variable in this experiment?",
          options: [
            "Corn yield",
            "Presence or absence of nitrogen fertilizer",
            "Plot size",
            "Corn variety"
          ],
          correct: 1,
          explanation: "The independent variable is what the experimenter deliberately manipulates. Here, the farmer controls whether plots receive fertilizer or not, making fertilizer application the independent variable."
        },
        {
          question: "What is the dependent variable?",
          options: [
            "Amount of nitrogen fertilizer",
            "Number of plots",
            "Corn yield (kg per plot)",
            "Time of harvest"
          ],
          correct: 2,
          explanation: "The dependent variable is what is measured as a result of the manipulation. Corn yield is measured to see how it responds to fertilizer application, making it the dependent variable."
        },
        {
          question: "Which of the following is a controlled variable?",
          options: [
            "Fertilizer application",
            "Corn yield",
            "Corn variety planted in all plots",
            "Difference between fertilized and unfertilized plots"
          ],
          correct: 2,
          explanation: "Controlled variables are kept constant to ensure they don't affect results. Using the same corn variety in all plots controls for genetic differences, ensuring that yield differences are due to fertilizer, not plant genetics."
        }
      ]
    },
    {
      id: "si-2-2",
      title: "Exercise and Heart Rate",
      context: "A physiologist studies how exercise intensity affects heart rate recovery time. Ten participants perform three exercise sessions on different days: low intensity (walking), moderate intensity (jogging), and high intensity (sprinting). Each session lasts 10 minutes. Immediately after exercise, participants rest while heart rate is monitored. The time required for heart rate to return to resting baseline is recorded.",
      questions: [
        {
          question: "What is the independent variable?",
          options: [
            "Heart rate recovery time",
            "Exercise intensity (low, moderate, high)",
            "Number of participants",
            "Resting heart rate"
          ],
          correct: 1,
          explanation: "Exercise intensity is deliberately varied by the experimenter (walking vs. jogging vs. sprinting) to observe its effect on recovery time. This makes it the independent variable."
        },
        {
          question: "What is the dependent variable?",
          options: [
            "Exercise intensity",
            "Duration of exercise session",
            "Time required for heart rate to return to baseline",
            "Number of exercise sessions"
          ],
          correct: 2,
          explanation: "Recovery time is measured as a result of different exercise intensities. It depends on the independent variable (intensity), making it the dependent variable."
        },
        {
          question: "Which factor is controlled to ensure valid comparisons?",
          options: [
            "Exercise intensity",
            "Recovery time",
            "Duration of exercise session (10 minutes for all)",
            "Heart rate during exercise"
          ],
          correct: 2,
          explanation: "Exercise duration is kept constant at 10 minutes for all intensity levels. This ensures that differences in recovery time are due to intensity, not duration. If sessions were different lengths, results would be confounded."
        }
      ]
    },
    {
      id: "si-2-3",
      title: "Caffeine and Reaction Time",
      context: "A psychologist investigates whether caffeine improves reaction time. Twenty participants are randomly assigned to two groups. Group A receives a beverage containing 200 mg of caffeine, while Group B receives an identical-tasting beverage with no caffeine (placebo). After 30 minutes, all participants complete a computer-based reaction time test where they press a button as quickly as possible when a stimulus appears. Average reaction time is calculated for each participant.",
      questions: [
        {
          question: "What is the independent variable?",
          options: [
            "Reaction time",
            "Presence or absence of caffeine",
            "Number of participants",
            "Type of computer test"
          ],
          correct: 1,
          explanation: "The experimenter controls whether participants receive caffeine or placebo. This manipulation of caffeine presence is the independent variable."
        },
        {
          question: "What is the dependent variable?",
          options: [
            "Caffeine dose",
            "Time waited after drinking beverage",
            "Average reaction time on the test",
            "Number of button presses"
          ],
          correct: 2,
          explanation: "Reaction time is measured to see how it responds to caffeine. It depends on whether caffeine was consumed, making it the dependent variable."
        },
        {
          question: "Why is it important that both beverages taste identical?",
          options: [
            "To make the experiment more expensive",
            "To control for the placebo effect—participants shouldn't know which beverage they received",
            "To ensure participants drink the entire beverage",
            "To test whether taste affects reaction time"
          ],
          correct: 1,
          explanation: "If participants knew whether they received caffeine, expectations could affect performance (placebo effect). Identical-tasting beverages keep participants blind to their group, ensuring that results reflect caffeine's actual effect, not expectations."
        }
      ]
    },
    {
      id: "si-2-4",
      title: "Soap and Bacterial Growth",
      context: "A microbiologist tests whether antibacterial soap is more effective than regular soap at reducing bacteria on hands. Twenty volunteers wash their hands with antibacterial soap, and twenty different volunteers wash with regular soap. All participants wash for 20 seconds with warm water. Immediately after washing, hand swabs are taken and bacteria are cultured on agar plates. After 24 hours of incubation at 37°C, bacterial colony counts are compared.",
      questions: [
        {
          question: "What is the independent variable?",
          options: [
            "Bacterial colony count",
            "Type of soap (antibacterial vs. regular)",
            "Water temperature",
            "Washing duration"
          ],
          correct: 1,
          explanation: "The experimenter assigns participants to use either antibacterial or regular soap. This deliberate manipulation of soap type is the independent variable."
        },
        {
          question: "What is the dependent variable?",
          options: [
            "Type of soap used",
            "Number of volunteers",
            "Bacterial colony count after washing",
            "Incubation temperature"
          ],
          correct: 2,
          explanation: "Bacterial colony count is measured to determine soap effectiveness. It depends on which soap was used, making it the dependent variable."
        },
        {
          question: "Which of the following is a controlled variable?",
          options: [
            "Type of soap",
            "Bacterial count",
            "Washing duration (20 seconds for all participants)",
            "Difference between the two groups"
          ],
          correct: 2,
          explanation: "Washing duration is kept constant at 20 seconds for all participants. This controls for the effect of washing time, ensuring that differences in bacterial reduction are due to soap type, not how long people washed."
        }
      ]
    },
    {
      id: "si-2-5",
      title: "Music and Plant Growth",
      context: "A student hypothesizes that playing classical music to plants will increase their growth rate. She places 30 identical bean seedlings in a growth chamber. Fifteen plants are exposed to classical music played continuously at 60 decibels, while fifteen plants are kept in silence. All plants receive the same light (12 hours/day), temperature (22°C), and water (50 mL daily). After 4 weeks, plant height is measured.",
      questions: [
        {
          question: "What is the independent variable?",
          options: [
            "Plant height",
            "Presence or absence of music",
            "Type of plant",
            "Amount of water"
          ],
          correct: 1,
          explanation: "The student controls whether plants are exposed to music or silence. This manipulation of music exposure is the independent variable."
        },
        {
          question: "What is the dependent variable?",
          options: [
            "Music volume",
            "Number of plants",
            "Plant height after 4 weeks",
            "Duration of music exposure"
          ],
          correct: 2,
          explanation: "Plant height is measured to see whether it responds to music exposure. It depends on the independent variable (music presence), making it the dependent variable."
        },
        {
          question: "Why is it important to control light, temperature, and water?",
          options: [
            "To make the plants grow faster",
            "To ensure these factors don't vary between groups and confound results",
            "To save resources",
            "To test multiple variables at once"
          ],
          correct: 1,
          explanation: "Light, temperature, and water all affect plant growth. If these varied between groups, observed differences might be due to these factors rather than music. Controlling them ensures music is the only variable being tested."
        }
      ]
    },
    {
      id: "si-2-6",
      title: "Salt Concentration and Boiling Point",
      context: "A chemist investigates how salt concentration affects water's boiling point. She prepares five beakers, each containing 500 mL of water with different amounts of dissolved salt: 0 g (pure water), 10 g, 20 g, 30 g, and 40 g. Each beaker is heated on an identical hot plate set to maximum heat. The temperature at which vigorous boiling begins is recorded for each solution.",
      questions: [
        {
          question: "What is the independent variable?",
          options: [
            "Boiling point temperature",
            "Salt concentration (amount of salt dissolved)",
            "Volume of water",
            "Heating time"
          ],
          correct: 1,
          explanation: "The chemist deliberately varies the amount of salt dissolved in each beaker. This manipulation of salt concentration is the independent variable."
        },
        {
          question: "What is the dependent variable?",
          options: [
            "Amount of salt added",
            "Water volume",
            "Boiling point temperature",
            "Hot plate setting"
          ],
          correct: 2,
          explanation: "Boiling point temperature is measured to see how it responds to different salt concentrations. It depends on salt concentration, making it the dependent variable."
        },
        {
          question: "Which factor is controlled to ensure valid comparisons?",
          options: [
            "Salt concentration",
            "Boiling point",
            "Volume of water (500 mL in all beakers)",
            "Time of day"
          ],
          correct: 2,
          explanation: "Water volume is kept constant at 500 mL. This is important because the amount of liquid affects heating time and boiling behavior. Controlling volume ensures that differences in boiling point are due to salt concentration alone."
        }
      ]
    },
    {
      id: "si-2-7",
      title: "Tire Pressure and Fuel Efficiency",
      context: "An automotive engineer tests whether tire pressure affects fuel efficiency. She uses the same car for all tests, driving a standardized 100-mile highway route at constant speed (65 mph). The test is repeated five times with different tire pressures: 25 psi, 30 psi, 35 psi (manufacturer recommended), 40 psi, and 45 psi. Fuel consumption is measured for each trip, and fuel efficiency (miles per gallon) is calculated.",
      questions: [
        {
          question: "What is the independent variable?",
          options: [
            "Fuel efficiency",
            "Tire pressure",
            "Distance driven",
            "Driving speed"
          ],
          correct: 1,
          explanation: "The engineer deliberately changes tire pressure for each test. This manipulation of tire pressure is the independent variable."
        },
        {
          question: "What is the dependent variable?",
          options: [
            "Tire pressure",
            "Route distance",
            "Fuel efficiency (miles per gallon)",
            "Number of tests"
          ],
          correct: 2,
          explanation: "Fuel efficiency is measured to see how it responds to different tire pressures. It depends on tire pressure, making it the dependent variable."
        },
        {
          question: "Why is it important to use the same car and route for all tests?",
          options: [
            "To save money on fuel",
            "To control for vehicle characteristics and driving conditions that could affect fuel efficiency",
            "To make the experiment faster",
            "To test only one car model"
          ],
          correct: 1,
          explanation: "Different cars have different fuel efficiencies, and different routes have different terrain and traffic. Using the same car and route controls these variables, ensuring that observed differences are due to tire pressure alone."
        }
      ]
    },
    {
      id: "si-2-8",
      title: "Detergent Temperature and Cleaning",
      context: "A consumer scientist tests whether water temperature affects laundry detergent cleaning effectiveness. She prepares 15 identical white cloths stained with the same amount of tomato sauce. Five cloths are washed in cold water (15°C), five in warm water (40°C), and five in hot water (60°C). All cloths are washed with the same detergent amount, in the same washing machine, for the same duration (30 minutes). After washing, stain removal is assessed by measuring cloth reflectance with a colorimeter.",
      questions: [
        {
          question: "What is the independent variable?",
          options: [
            "Stain removal effectiveness",
            "Water temperature",
            "Amount of detergent",
            "Type of stain"
          ],
          correct: 1,
          explanation: "The scientist deliberately varies water temperature (cold, warm, hot) to observe its effect on cleaning. This manipulation of temperature is the independent variable."
        },
        {
          question: "What is the dependent variable?",
          options: [
            "Water temperature",
            "Washing duration",
            "Stain removal (measured by cloth reflectance)",
            "Number of cloths"
          ],
          correct: 2,
          explanation: "Stain removal is measured (via reflectance) to see how it responds to different water temperatures. It depends on temperature, making it the dependent variable."
        },
        {
          question: "Why is it important that all cloths have the same amount of tomato sauce stain?",
          options: [
            "To make the cloths look identical",
            "To control for initial stain severity, ensuring differences in cleaning are due to temperature, not stain amount",
            "To use up all the tomato sauce",
            "To test whether tomato sauce is hard to remove"
          ],
          correct: 1,
          explanation: "If stain amounts varied, some cloths might be easier to clean simply because they had less stain. Standardizing stain amount ensures that differences in cleaning effectiveness are due to water temperature, not initial stain severity."
        }
      ]
    },
    {
      id: "si-2-9",
      title: "Screen Time and Sleep Quality",
      context: "A sleep researcher investigates whether screen time before bed affects sleep quality. Thirty participants are divided into three groups. Group A avoids all screens for 2 hours before bed. Group B uses screens (phone, tablet, computer) for 1 hour before bed. Group C uses screens for 2 hours before bed. All participants go to bed at 11 PM and wake at 7 AM. Sleep quality is measured using wrist-worn sleep trackers that monitor sleep stages and restlessness.",
      questions: [
        {
          question: "What is the independent variable?",
          options: [
            "Sleep quality",
            "Duration of screen time before bed",
            "Bedtime",
            "Number of participants"
          ],
          correct: 1,
          explanation: "The researcher assigns participants to different screen time durations (0, 1, or 2 hours). This manipulation of screen time duration is the independent variable."
        },
        {
          question: "What is the dependent variable?",
          options: [
            "Screen time duration",
            "Wake time",
            "Sleep quality (measured by sleep tracker)",
            "Type of screen used"
          ],
          correct: 2,
          explanation: "Sleep quality is measured to see how it responds to different screen time durations. It depends on screen time, making it the dependent variable."
        },
        {
          question: "Why is it important that all participants go to bed at 11 PM and wake at 7 AM?",
          options: [
            "To ensure everyone gets 8 hours of sleep opportunity, controlling for sleep duration",
            "To make data collection easier",
            "To test whether bedtime matters",
            "To match typical sleep schedules"
          ],
          correct: 0,
          explanation: "Standardizing bedtime and wake time ensures all participants have the same sleep opportunity (8 hours). This controls for sleep duration, ensuring that differences in sleep quality are due to screen time, not how long people slept."
        }
      ]
    },
    {
      id: "si-2-10",
      title: "Mulch Type and Soil Moisture",
      context: "A horticulturist tests how different mulch types affect soil moisture retention. She prepares 16 identical garden plots, each 1 meter square. Four plots receive wood chip mulch, four receive straw mulch, four receive rubber mulch, and four receive no mulch (control). All plots are watered equally (10 liters) at the start. After 7 days with no additional watering, soil moisture is measured at 10 cm depth using a moisture meter.",
      questions: [
        {
          question: "What is the independent variable?",
          options: [
            "Soil moisture level",
            "Type of mulch (wood chips, straw, rubber, or none)",
            "Plot size",
            "Measurement depth"
          ],
          correct: 1,
          explanation: "The horticulturist assigns different mulch types to different plots. This manipulation of mulch type is the independent variable."
        },
        {
          question: "What is the dependent variable?",
          options: [
            "Mulch type",
            "Amount of water applied",
            "Soil moisture level after 7 days",
            "Number of plots"
          ],
          correct: 2,
          explanation: "Soil moisture is measured to see how it responds to different mulch types. It depends on mulch type, making it the dependent variable."
        },
        {
          question: "What is the purpose of the four plots with no mulch?",
          options: [
            "To save mulch for other experiments",
            "To serve as a control group for comparison, showing moisture retention without mulch",
            "To test whether mulch is necessary",
            "To use all available plots"
          ],
          correct: 1,
          explanation: "The no-mulch plots serve as a control, showing baseline moisture retention without mulch. This allows the researcher to determine whether each mulch type retains more or less moisture than bare soil."
        }
      ]
    }
  ]
};